import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

export const OrbBreakoutAnimation = () => {
  const canvasRef = useRef(null);
  const [breakoutDirection, setBreakoutDirection] = useState('long'); // 'long' or 'short'
  const animationRef = useRef(null);
  const frameRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;

    // Animation parameters
    let frame = 0;
    const totalFrames = 300;
    const rangeStart = 60;
    const rangeEnd = 120;
    const breakoutStart = 140;

    // Price data generation
    const generatePriceData = (currentFrame) => {
      const points = [];
      const basePrice = height * 0.5;
      const rangeHeight = height * 0.15;
      const rangeTop = basePrice - rangeHeight;
      const rangeBottom = basePrice + rangeHeight;

      for (let i = 0; i <= Math.min(currentFrame, width - 40); i++) {
        let price;
        
        if (i < rangeStart) {
          // Pre-range: approaching the range
          const progress = i / rangeStart;
          price = basePrice + Math.sin(i * 0.1) * 20 * (1 - progress * 0.5);
        } else if (i >= rangeStart && i < rangeEnd) {
          // Inside range: consolidation
          const rangeProgress = (i - rangeStart) / (rangeEnd - rangeStart);
          const oscillation = Math.sin(i * 0.15) * rangeHeight * 0.6;
          price = basePrice + oscillation * (1 - rangeProgress * 0.3);
        } else if (i >= rangeEnd && i < breakoutStart) {
          // Compression before breakout
          const compressionProgress = (i - rangeEnd) / (breakoutStart - rangeEnd);
          price = basePrice + Math.sin(i * 0.2) * rangeHeight * 0.3 * (1 - compressionProgress);
        } else {
          // Breakout!
          const breakoutProgress = (i - breakoutStart) / (width - breakoutStart);
          const momentum = Math.pow(breakoutProgress, 0.7);
          
          if (breakoutDirection === 'long') {
            price = basePrice - momentum * height * 0.35;
            // Add some volatility
            price += Math.sin(i * 0.3) * 5 * (1 - momentum * 0.5);
          } else {
            price = basePrice + momentum * height * 0.35;
            price += Math.sin(i * 0.3) * 5 * (1 - momentum * 0.5);
          }
        }
        
        points.push({ x: i + 20, y: price });
      }
      
      return { points, rangeTop, rangeBottom, basePrice };
    };

    const drawGrid = () => {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
      ctx.lineWidth = 1;

      // Vertical lines
      for (let x = 0; x < width; x += 30) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      // Horizontal lines
      for (let y = 0; y < height; y += 30) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    };

    const drawOpeningRange = (rangeTop, rangeBottom, currentFrame) => {
      if (currentFrame < rangeStart) return;

      const rangeWidth = Math.min(currentFrame - rangeStart, rangeEnd - rangeStart);
      const alpha = Math.min(1, rangeWidth / 20);

      // Opening Range Box with 3D effect
      const gradient = ctx.createLinearGradient(rangeStart + 20, rangeTop, rangeStart + 20, rangeBottom);
      gradient.addColorStop(0, `rgba(245, 158, 11, ${0.15 * alpha})`);
      gradient.addColorStop(0.5, `rgba(245, 158, 11, ${0.08 * alpha})`);
      gradient.addColorStop(1, `rgba(245, 158, 11, ${0.15 * alpha})`);

      ctx.fillStyle = gradient;
      ctx.fillRect(rangeStart + 20, rangeTop, rangeWidth, rangeBottom - rangeTop);

      // Range borders
      ctx.strokeStyle = `rgba(245, 158, 11, ${0.6 * alpha})`;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      
      // Top line (resistance)
      ctx.beginPath();
      ctx.moveTo(rangeStart + 20, rangeTop);
      ctx.lineTo(width - 20, rangeTop);
      ctx.stroke();

      // Bottom line (support)
      ctx.beginPath();
      ctx.moveTo(rangeStart + 20, rangeBottom);
      ctx.lineTo(width - 20, rangeBottom);
      ctx.stroke();

      ctx.setLineDash([]);

      // Labels
      if (alpha > 0.5) {
        ctx.font = '10px monospace';
        ctx.fillStyle = `rgba(245, 158, 11, ${alpha})`;
        ctx.fillText('RANGE HIGH', rangeEnd + 30, rangeTop - 5);
        ctx.fillText('RANGE LOW', rangeEnd + 30, rangeBottom + 15);
        
        // Opening Range label
        ctx.font = 'bold 11px monospace';
        ctx.fillStyle = `rgba(245, 158, 11, ${alpha * 0.8})`;
        ctx.fillText('OPENING RANGE', rangeStart + 25, rangeTop - 15);
      }
    };

    const drawBreakoutZone = (rangeTop, rangeBottom, currentFrame) => {
      if (currentFrame < breakoutStart) return;

      const breakoutProgress = Math.min(1, (currentFrame - breakoutStart) / 50);
      
      // Breakout arrow and zone
      const isLong = breakoutDirection === 'long';
      const targetY = isLong ? rangeTop - 60 : rangeBottom + 60;
      const startY = isLong ? rangeTop : rangeBottom;

      // Glow effect
      const glowGradient = ctx.createRadialGradient(
        breakoutStart + 40, startY, 0,
        breakoutStart + 40, startY, 80
      );
      glowGradient.addColorStop(0, isLong ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)');
      glowGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = glowGradient;
      ctx.beginPath();
      ctx.arc(breakoutStart + 40, startY, 80 * breakoutProgress, 0, Math.PI * 2);
      ctx.fill();

      // Breakout label
      if (breakoutProgress > 0.3) {
        ctx.font = 'bold 12px monospace';
        ctx.fillStyle = isLong ? 'rgba(16, 185, 129, 0.9)' : 'rgba(239, 68, 68, 0.9)';
        ctx.fillText(
          isLong ? 'BREAKOUT LONG' : 'BREAKOUT SHORT',
          breakoutStart + 60,
          isLong ? rangeTop - 40 : rangeBottom + 50
        );
      }
    };

    const drawPriceLine = (points, currentFrame) => {
      if (points.length < 2) return;

      // Create gradient for the line
      const gradient = ctx.createLinearGradient(0, 0, width, 0);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
      gradient.addColorStop(0.4, 'rgba(245, 158, 11, 0.8)');
      
      if (currentFrame > breakoutStart) {
        if (breakoutDirection === 'long') {
          gradient.addColorStop(0.6, 'rgba(16, 185, 129, 1)');
          gradient.addColorStop(1, 'rgba(16, 185, 129, 1)');
        } else {
          gradient.addColorStop(0.6, 'rgba(239, 68, 68, 1)');
          gradient.addColorStop(1, 'rgba(239, 68, 68, 1)');
        }
      } else {
        gradient.addColorStop(1, 'rgba(245, 158, 11, 0.8)');
      }

      // Glow effect
      ctx.shadowColor = currentFrame > breakoutStart 
        ? (breakoutDirection === 'long' ? '#10B981' : '#EF4444')
        : '#F59E0B';
      ctx.shadowBlur = 15;

      // Draw main line
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      
      for (let i = 1; i < points.length; i++) {
        const xc = (points[i].x + points[i - 1].x) / 2;
        const yc = (points[i].y + points[i - 1].y) / 2;
        ctx.quadraticCurveTo(points[i - 1].x, points[i - 1].y, xc, yc);
      }
      
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Draw current price dot
      if (points.length > 0) {
        const lastPoint = points[points.length - 1];
        
        // Pulse effect
        const pulse = Math.sin(currentFrame * 0.1) * 3 + 8;
        
        ctx.beginPath();
        ctx.arc(lastPoint.x, lastPoint.y, pulse, 0, Math.PI * 2);
        ctx.fillStyle = currentFrame > breakoutStart 
          ? (breakoutDirection === 'long' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)')
          : 'rgba(245, 158, 11, 0.3)';
        ctx.fill();

        ctx.beginPath();
        ctx.arc(lastPoint.x, lastPoint.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = currentFrame > breakoutStart 
          ? (breakoutDirection === 'long' ? '#10B981' : '#EF4444')
          : '#F59E0B';
        ctx.fill();

        // Price label
        ctx.font = 'bold 11px monospace';
        ctx.fillStyle = '#fff';
        const priceValue = (100 - (lastPoint.y / height) * 50 + 75).toFixed(2);
        ctx.fillText(`$${priceValue}`, lastPoint.x + 15, lastPoint.y + 4);
      }
    };

    const drawProfitTarget = (rangeTop, rangeBottom, currentFrame) => {
      if (currentFrame < breakoutStart + 30) return;

      const isLong = breakoutDirection === 'long';
      const rangeSize = rangeBottom - rangeTop;
      const targetY = isLong ? rangeTop - rangeSize * 1.5 : rangeBottom + rangeSize * 1.5;
      const alpha = Math.min(1, (currentFrame - breakoutStart - 30) / 30);

      // TP line
      ctx.strokeStyle = `rgba(16, 185, 129, ${alpha * 0.8})`;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(breakoutStart + 20, targetY);
      ctx.lineTo(width - 20, targetY);
      ctx.stroke();
      ctx.setLineDash([]);

      // TP label
      ctx.font = 'bold 10px monospace';
      ctx.fillStyle = `rgba(16, 185, 129, ${alpha})`;
      ctx.fillText('TAKE PROFIT (1.5R)', width - 120, targetY - 5);

      // SL line
      const slY = isLong ? rangeBottom + 10 : rangeTop - 10;
      ctx.strokeStyle = `rgba(239, 68, 68, ${alpha * 0.6})`;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(breakoutStart + 20, slY);
      ctx.lineTo(width - 20, slY);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = `rgba(239, 68, 68, ${alpha})`;
      ctx.fillText('STOP LOSS', width - 90, slY + 15);
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw components
      drawGrid();
      
      const { points, rangeTop, rangeBottom } = generatePriceData(frame);
      
      drawOpeningRange(rangeTop, rangeBottom, frame);
      drawBreakoutZone(rangeTop, rangeBottom, frame);
      drawProfitTarget(rangeTop, rangeBottom, frame);
      drawPriceLine(points, frame);

      frame++;
      if (frame > totalFrames) {
        frame = 0;
        // Alternate between long and short
        setBreakoutDirection(prev => prev === 'long' ? 'short' : 'long');
      }

      frameRef.current = frame;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [breakoutDirection]);

  return (
    <motion.div
      initial={{ opacity: 0, rotateY: -15 }}
      animate={{ opacity: 1, rotateY: 0 }}
      transition={{ duration: 1, delay: 0.5 }}
      className="relative w-full max-w-2xl mx-auto"
      style={{ perspective: '1000px' }}
    >
      {/* 3D Container */}
      <motion.div
        animate={{ 
          rotateX: [0, 2, 0, -2, 0],
          rotateY: [0, -3, 0, 3, 0]
        }}
        transition={{ 
          duration: 8, 
          repeat: Infinity, 
          ease: "easeInOut" 
        }}
        className="relative"
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Glow effect behind */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#F59E0B]/20 via-[#10B981]/20 to-[#F59E0B]/20 blur-3xl rounded-3xl transform -translate-z-10" />
        
        {/* Main canvas container */}
        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a12]/80 backdrop-blur-sm shadow-2xl">
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/5">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs font-mono">NAS100 • 5m</span>
              <div className={`w-2 h-2 rounded-full animate-pulse ${
                breakoutDirection === 'long' ? 'bg-green-500' : 'bg-red-500'
              }`} />
            </div>
          </div>

          {/* Canvas */}
          <canvas
            ref={canvasRef}
            className="w-full h-[280px] md:h-[320px]"
            style={{ display: 'block' }}
          />

          {/* Bottom info bar */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 bg-white/5">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-1 bg-[#F59E0B] rounded" />
                <span className="text-white/50 text-xs">Range</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-1 bg-[#10B981] rounded" />
                <span className="text-white/50 text-xs">Long</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-1 bg-red-500 rounded" />
                <span className="text-white/50 text-xs">Short</span>
              </div>
            </div>
            <span className="text-white/30 text-xs font-mono">LIVE VISUALIZATION</span>
          </div>
        </div>

        {/* Floating labels */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5 }}
          className="absolute -left-4 top-1/3 transform -translate-x-full hidden lg:block"
        >
          <div className="glass px-3 py-2 rounded-lg border border-[#F59E0B]/30">
            <div className="text-[#F59E0B] text-xs font-mono">RANGE WINDOW</div>
            <div className="text-white/50 text-[10px]">Defining levels</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.8 }}
          className="absolute -right-4 top-1/2 transform translate-x-full hidden lg:block"
        >
          <div className={`glass px-3 py-2 rounded-lg border ${
            breakoutDirection === 'long' ? 'border-[#10B981]/30' : 'border-red-500/30'
          }`}>
            <div className={`text-xs font-mono ${
              breakoutDirection === 'long' ? 'text-[#10B981]' : 'text-red-400'
            }`}>
              {breakoutDirection === 'long' ? 'BULLISH' : 'BEARISH'}
            </div>
            <div className="text-white/50 text-[10px]">BREAKOUT</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Strategy explanation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2 }}
        className="flex flex-wrap justify-center gap-4 mt-6"
      >
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          <span className="text-[#F59E0B] text-lg">1</span>
          <span className="text-white/60 text-xs">Identify Range</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          <span className="text-[#F59E0B] text-lg">2</span>
          <span className="text-white/60 text-xs">Wait for Breakout</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          <span className="text-[#10B981] text-lg">3</span>
          <span className="text-white/60 text-xs">Ride the Momentum</span>
        </div>
      </motion.div>
    </motion.div>
  );
};
